/*
 * countdown-bar-offset.js (loaded deferred by sections/header.liquid)
 *
 * Stacks the theme's sticky header BELOW the GetSiteControl countdown bar instead of under it.
 *
 * The countdown bar (app embed "gsc-countdown-timer") is added by the app about 2 s after load as the
 * FIRST child of <body>: div.gta-widget.gta-bar (one per enabled bar widget; a bottom bar is appended
 * last instead). The app renders its own style element INSIDE the bar: position: sticky (relative when
 * the app's sticky option is off), top: 0 for a top bar (unset for a bottom bar), z-index: 90. A closed
 * bar is left in the DOM empty, unstyled and 0 px tall. The header section is sticky at top 0 with
 * z-index 4, so on its own it slides under the bar.
 *
 * This file publishes the bar's height as --countdown-bar-height on <html>, ONLY while a bar is pinned
 * to the top of the viewport (sticky or fixed, top 0). Several top bars all pin at top 0 and overlap,
 * so the TALLEST pinned one is published (not the sum). The header's sticky top
 * (sections/header.liquid), --sticky-area-height and --sticky-offset (snippets/css-variables.liquid),
 * the product gallery, the mega-menu overlay and section anchors (assets/theme.css) add it with a 0px
 * fallback.
 *
 * Fail-safe: no bar, a closed bar, a bottom bar or a non-sticky bar -> the property is REMOVED and
 * every consumer falls back to 0px, exactly as before this file existed. Without a bar the only work
 * is one selector query per change of <body>'s direct children.
 *
 * After every change of the value, the theme.js components that cache the sticky offset are refreshed:
 * - <safe-sticky> (product info, collection sidebar) and <reading-text> (its letter-reveal timeline)
 *   re-read it only when their own size changes, so each gets a 1px, one-frame size nudge;
 * - an open <header-menu-drawer> / <header-menu-dropdown-sidebar> sizes its panel from a stored
 *   --menu-offset-top that it re-measures only on scroll, so it is re-measured here the same way;
 * - a "countdown-bar:change" event is dispatched on document (detail.height in px, 0 when unset) for
 *   section scripts that read the value once (sections/reading-text.liquid rebuilds its timeline).
 */
(function () {
  'use strict';

  if (window.__countdownBarOffset) return;
  window.__countdownBarOffset = true;
  if (!('ResizeObserver' in window) || !('MutationObserver' in window) || !document.body) return;

  var SELECTOR = 'body > .gta-widget.gta-bar';
  var NAME = '--countdown-bar-height';
  var EVENT = 'countdown-bar:change';
  var root = document.documentElement;
  var bars = [];
  var sizes = new Map();
  var current = '';
  var queued = false;

  var resizeObserver = new ResizeObserver(function (entries) {
    entries.forEach(function (entry) {
      var box = entry.borderBoxSize && (entry.borderBoxSize[0] || entry.borderBoxSize);
      sizes.set(entry.target, box && typeof box.blockSize === 'number' ? box.blockSize : entry.target.getBoundingClientRect().height);
    });
    schedule();
  });

  // A bar's own style element comes and goes with its content; class/style may change on close.
  var barObserver = new MutationObserver(schedule);

  function pinnedHeight(bar) {
    if (!bar.isConnected) return 0;
    var style = getComputedStyle(bar);
    if (style.display === 'none') return 0;
    if (style.position !== 'sticky' && style.position !== 'fixed') return 0;
    if (style.top !== '0px') return 0;
    return sizes.has(bar) ? sizes.get(bar) : bar.getBoundingClientRect().height;
  }

  function nudgeSize(elements) {
    // A 1px size change fires the element's own ResizeObserver in theme.js, which makes it re-read its
    // sticky offset (now with or without the bar). The inline min-height is restored on the next frame.
    if (!elements.length) return;
    var saved = [];
    var heights = [];
    elements.forEach(function (el) {
      saved.push([el.style.getPropertyValue('min-height'), el.style.getPropertyPriority('min-height')]);
      heights.push(el.getBoundingClientRect().height);
    });
    elements.forEach(function (el, i) {
      el.style.setProperty('min-height', (heights[i] + 1) + 'px', 'important');
    });
    requestAnimationFrame(function () {
      elements.forEach(function (el, i) {
        if (saved[i][0]) {
          el.style.setProperty('min-height', saved[i][0], saved[i][1]);
        } else {
          el.style.removeProperty('min-height');
        }
      });
    });
  }

  function refreshMenuOffsets() {
    // Same measurement as theme.js calculateOffsets for an open menu drawer / dropdown sidebar.
    document.querySelectorAll('header-menu-drawer[open], header-menu-dropdown-sidebar[open]').forEach(function (menu) {
      var content = menu.shadowRoot && menu.shadowRoot.querySelector('[part="content"]');
      if (content) menu.style.setProperty('--menu-offset-top', content.getBoundingClientRect().top + 'px');
    });
  }

  function update() {
    queued = false;
    var height = 0;
    bars.forEach(function (bar) {
      height = Math.max(height, pinnedHeight(bar));
    });
    var value = height >= 1 ? height.toFixed(2) + 'px' : '';
    if (value === current) return;
    current = value;
    if (value) {
      root.style.setProperty(NAME, value);
    } else {
      root.style.removeProperty(NAME);
    }
    refreshMenuOffsets();
    nudgeSize(Array.prototype.slice.call(document.querySelectorAll('safe-sticky, reading-text')));
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { height: value ? parseFloat(value) : 0 } }));
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  }

  function attach() {
    var found = Array.prototype.slice.call(document.querySelectorAll(SELECTOR));
    var same = found.length === bars.length && found.every(function (bar, i) {
      return bar === bars[i];
    });
    if (same) {
      if (bars.length) schedule();
      return;
    }
    bars.forEach(function (bar) {
      if (found.indexOf(bar) === -1) {
        resizeObserver.unobserve(bar);
        sizes.delete(bar);
      }
    });
    barObserver.disconnect();
    found.forEach(function (bar) {
      if (bars.indexOf(bar) === -1) resizeObserver.observe(bar);
      barObserver.observe(bar, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    });
    bars = found;
    schedule();
  }

  // The app adds a top bar as body's first child (a bottom bar as its last), and may remove or re-add it.
  new MutationObserver(attach).observe(document.body, { childList: true });
  attach();
})();
